import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';

// Public listing types based on design document
export interface PublicListing {
  listingId: string;
  title: string;
  description: string;
  address: {
    city: string;
    state: string;
    country: string;
    neighborhood?: string;
    // Street address and coordinates hidden for privacy
  };
  pricing: {
    basePrice: number;
    currency: string;
    cleaningFee?: number;
  };
  photos: string[]; // S3 URLs via CloudFront
  amenities: string[];
  maxGuests: number;
  bedrooms: number;
  bathrooms: number;
  propertyType?: string;
  roomType?: string;
  instantBook?: boolean;
  hostInfo: {
    hostId: string;
    hostName?: string;
    name?: string;
    verificationLevel?: string;
    responseRate?: number;
    responseTime?: string;
    // Additional host details fetched from UserProfile
  };
  createdAt: string;
}

export interface PropertySearchParams {
  location?: string;
  checkIn?: string; // ISO date string
  checkOut?: string; // ISO date string
  guests?: number;
  priceMin?: number;
  priceMax?: number;
  propertyTypes?: string[];
  amenities?: string[];
  instantBook?: boolean;
  sortBy?: 'price_asc' | 'price_desc' | 'newest' | 'rating';
  limit?: number;
  offset?: number;
}

export interface PropertySearchResponse {
  listings: PublicListing[];
  totalCount: number;
  hasMore: boolean;
  filters: {
    availableAmenities: string[];
    priceRange: { min: number; max: number };
    propertyTypes: string[];
  };
}

export interface SearchFilters {
  priceRange: { min: number; max: number };
  propertyTypes: string[];
  amenities: string[];
  instantBook?: boolean;
}

// Search state interface
interface SearchState {
  // Search parameters and results
  searchParams: PropertySearchParams;
  searchResults: PublicListing[];
  isLoading: boolean;
  hasMore: boolean;
  totalCount: number;
  error: string | null;
  
  // Filters and UI state
  activeFilters: SearchFilters;
  availableFilters: {
    amenities: string[];
    propertyTypes: string[];
    priceRange: { min: number; max: number };
  };
  
  // Local storage integration
  recentSearches: PropertySearchParams[];
  favoriteListings: string[]; // listing IDs
  
  // UI state
  isSearchModalOpen: boolean;
  isFilterModalOpen: boolean;
  selectedListing?: PublicListing;
}

// Action types
type SearchAction =
  | { type: 'SET_SEARCH_PARAMS'; payload: PropertySearchParams }
  | { type: 'SET_SEARCH_RESULTS'; payload: { listings: PublicListing[]; totalCount: number; hasMore: boolean } }
  | { type: 'APPEND_SEARCH_RESULTS'; payload: { listings: PublicListing[]; hasMore: boolean } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ACTIVE_FILTERS'; payload: SearchFilters }
  | { type: 'SET_AVAILABLE_FILTERS'; payload: { amenities: string[]; propertyTypes: string[]; priceRange: { min: number; max: number } } }
  | { type: 'ADD_RECENT_SEARCH'; payload: PropertySearchParams }
  | { type: 'SET_RECENT_SEARCHES'; payload: PropertySearchParams[] }
  | { type: 'TOGGLE_FAVORITE'; payload: string }
  | { type: 'SET_FAVORITES'; payload: string[] }
  | { type: 'SET_SEARCH_MODAL_OPEN'; payload: boolean }
  | { type: 'SET_FILTER_MODAL_OPEN'; payload: boolean }
  | { type: 'SET_SELECTED_LISTING'; payload: PublicListing | undefined }
  | { type: 'CLEAR_SEARCH_RESULTS' }
  | { type: 'RESET_SEARCH' };

// Initial state
const initialState: SearchState = {
  searchParams: {},
  searchResults: [],
  isLoading: false,
  hasMore: false,
  totalCount: 0,
  error: null,
  activeFilters: {
    priceRange: { min: 0, max: 1000 },
    propertyTypes: [],
    amenities: [],
    instantBook: undefined,
  },
  availableFilters: {
    amenities: [],
    propertyTypes: [],
    priceRange: { min: 0, max: 1000 },
  },
  recentSearches: [],
  favoriteListings: [],
  isSearchModalOpen: false,
  isFilterModalOpen: false,
  selectedListing: undefined,
};

// Reducer function
function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SET_SEARCH_PARAMS':
      return {
        ...state,
        searchParams: action.payload,
      };
    
    case 'SET_SEARCH_RESULTS':
      return {
        ...state,
        searchResults: action.payload.listings,
        totalCount: action.payload.totalCount,
        hasMore: action.payload.hasMore,
        isLoading: false,
        error: null,
      };
    
    case 'APPEND_SEARCH_RESULTS':
      return {
        ...state,
        searchResults: [...state.searchResults, ...action.payload.listings],
        hasMore: action.payload.hasMore,
        isLoading: false,
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error, // Clear error when starting new load
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    
    case 'SET_ACTIVE_FILTERS':
      return {
        ...state,
        activeFilters: action.payload,
      };
    
    case 'SET_AVAILABLE_FILTERS':
      return {
        ...state,
        availableFilters: action.payload,
      };
    
    case 'ADD_RECENT_SEARCH':
      const newRecentSearches = [
        action.payload,
        ...state.recentSearches.filter(search => 
          JSON.stringify(search) !== JSON.stringify(action.payload)
        )
      ].slice(0, 10); // Keep only last 10 searches
      
      return {
        ...state,
        recentSearches: newRecentSearches,
      };
    
    case 'SET_RECENT_SEARCHES':
      return {
        ...state,
        recentSearches: action.payload,
      };
    
    case 'TOGGLE_FAVORITE':
      const listingId = action.payload;
      const isFavorite = state.favoriteListings.includes(listingId);
      const newFavorites = isFavorite
        ? state.favoriteListings.filter(id => id !== listingId)
        : [...state.favoriteListings, listingId];
      
      return {
        ...state,
        favoriteListings: newFavorites,
      };
    
    case 'SET_FAVORITES':
      return {
        ...state,
        favoriteListings: action.payload,
      };
    
    case 'SET_SEARCH_MODAL_OPEN':
      return {
        ...state,
        isSearchModalOpen: action.payload,
      };
    
    case 'SET_FILTER_MODAL_OPEN':
      return {
        ...state,
        isFilterModalOpen: action.payload,
      };
    
    case 'SET_SELECTED_LISTING':
      return {
        ...state,
        selectedListing: action.payload,
      };
    
    case 'CLEAR_SEARCH_RESULTS':
      return {
        ...state,
        searchResults: [],
        totalCount: 0,
        hasMore: false,
        error: null,
      };
    
    case 'RESET_SEARCH':
      return {
        ...initialState,
        recentSearches: state.recentSearches,
        favoriteListings: state.favoriteListings,
      };
    
    default:
      return state;
  }
}

// Context interface
interface SearchContextType {
  // State
  state: SearchState;
  
  // Search actions
  setSearchParams: (params: PropertySearchParams) => void;
  setSearchResults: (results: { listings: PublicListing[]; totalCount: number; hasMore: boolean }) => void;
  appendSearchResults: (results: { listings: PublicListing[]; hasMore: boolean }) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearSearchResults: () => void;
  resetSearch: () => void;
  
  // Filter actions
  setActiveFilters: (filters: SearchFilters) => void;
  setAvailableFilters: (filters: { amenities: string[]; propertyTypes: string[]; priceRange: { min: number; max: number } }) => void;
  
  // Local storage actions
  addRecentSearch: (params: PropertySearchParams) => void;
  toggleFavorite: (listingId: string) => void;
  
  // UI actions
  setSearchModalOpen: (open: boolean) => void;
  setFilterModalOpen: (open: boolean) => void;
  setSelectedListing: (listing: PublicListing | undefined) => void;
  
  // Utility functions
  isFavorite: (listingId: string) => boolean;
  hasActiveFilters: () => boolean;
  getActiveFilterCount: () => number;
}

// Create context
const SearchContext = createContext<SearchContextType | undefined>(undefined);

// Local storage keys
const STORAGE_KEYS = {
  RECENT_SEARCHES: 'bnb_recent_searches',
  FAVORITE_LISTINGS: 'bnb_favorite_listings',
} as const;

// Provider component
interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(searchReducer, initialState);

  // Load data from localStorage on mount
  useEffect(() => {
    try {
      const recentSearches = localStorage.getItem(STORAGE_KEYS.RECENT_SEARCHES);
      if (recentSearches) {
        const parsed = JSON.parse(recentSearches);
        dispatch({ type: 'SET_RECENT_SEARCHES', payload: parsed });
      }

      const favoriteListings = localStorage.getItem(STORAGE_KEYS.FAVORITE_LISTINGS);
      if (favoriteListings) {
        const parsed = JSON.parse(favoriteListings);
        dispatch({ type: 'SET_FAVORITES', payload: parsed });
      }
    } catch (error) {
      console.error('Error loading search data from localStorage:', error);
    }
  }, []);

  // Save recent searches to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.RECENT_SEARCHES, JSON.stringify(state.recentSearches));
    } catch (error) {
      console.error('Error saving recent searches to localStorage:', error);
    }
  }, [state.recentSearches]);

  // Save favorites to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.FAVORITE_LISTINGS, JSON.stringify(state.favoriteListings));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }, [state.favoriteListings]);

  // Action creators
  const setSearchParams = useCallback((params: PropertySearchParams) => {
    dispatch({ type: 'SET_SEARCH_PARAMS', payload: params });
  }, []);

  const setSearchResults = useCallback((results: { listings: PublicListing[]; totalCount: number; hasMore: boolean }) => {
    dispatch({ type: 'SET_SEARCH_RESULTS', payload: results });
  }, []);

  const appendSearchResults = useCallback((results: { listings: PublicListing[]; hasMore: boolean }) => {
    dispatch({ type: 'APPEND_SEARCH_RESULTS', payload: results });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const clearSearchResults = useCallback(() => {
    dispatch({ type: 'CLEAR_SEARCH_RESULTS' });
  }, []);

  const resetSearch = useCallback(() => {
    dispatch({ type: 'RESET_SEARCH' });
  }, []);

  const setActiveFilters = useCallback((filters: SearchFilters) => {
    dispatch({ type: 'SET_ACTIVE_FILTERS', payload: filters });
  }, []);

  const setAvailableFilters = useCallback((filters: { amenities: string[]; propertyTypes: string[]; priceRange: { min: number; max: number } }) => {
    dispatch({ type: 'SET_AVAILABLE_FILTERS', payload: filters });
  }, []);

  const addRecentSearch = useCallback((params: PropertySearchParams) => {
    // Only add to recent searches if it has meaningful search criteria
    if (params.location || params.checkIn || params.checkOut || params.guests) {
      dispatch({ type: 'ADD_RECENT_SEARCH', payload: params });
    }
  }, []);

  const toggleFavorite = useCallback((listingId: string) => {
    dispatch({ type: 'TOGGLE_FAVORITE', payload: listingId });
  }, []);

  const setSearchModalOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_SEARCH_MODAL_OPEN', payload: open });
  }, []);

  const setFilterModalOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_FILTER_MODAL_OPEN', payload: open });
  }, []);

  const setSelectedListing = useCallback((listing: PublicListing | undefined) => {
    dispatch({ type: 'SET_SELECTED_LISTING', payload: listing });
  }, []);

  // Utility functions
  const isFavorite = useCallback((listingId: string) => {
    return state.favoriteListings.includes(listingId);
  }, [state.favoriteListings]);

  const hasActiveFilters = useCallback(() => {
    return (
      state.activeFilters.propertyTypes.length > 0 ||
      state.activeFilters.amenities.length > 0 ||
      state.activeFilters.instantBook !== undefined ||
      state.activeFilters.priceRange.min > 0 ||
      state.activeFilters.priceRange.max < 1000
    );
  }, [state.activeFilters]);

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    
    if (state.activeFilters.propertyTypes.length > 0) count++;
    if (state.activeFilters.amenities.length > 0) count++;
    if (state.activeFilters.instantBook !== undefined) count++;
    if (state.activeFilters.priceRange.min > 0 || state.activeFilters.priceRange.max < 1000) count++;
    
    return count;
  }, [state.activeFilters]);

  const value: SearchContextType = {
    state,
    setSearchParams,
    setSearchResults,
    appendSearchResults,
    setLoading,
    setError,
    clearSearchResults,
    resetSearch,
    setActiveFilters,
    setAvailableFilters,
    addRecentSearch,
    toggleFavorite,
    setSearchModalOpen,
    setFilterModalOpen,
    setSelectedListing,
    isFavorite,
    hasActiveFilters,
    getActiveFilterCount,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

// Hook to use search context
export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export default SearchContext;